import { DaycareAvailabilityRoom } from 'types/Availability';
import { Pet } from 'types/Pet';
import { Service } from 'types/Service';
import { convertAvailabilityRoomToRoom } from 'utils/room/availabilityRoomConverter';
import { getRoomUnitAssignmentsForPets } from 'utils/room/roomUnitAssignment';

export interface BoardingServiceRoomUnitAssignment {
    roomId: number;
    roomUnitIds: Array<number>;
}

// Unlike daycare, boarding stores ROOM ids (not room-unit ids) in selectedRooms.
const getAssignedBoardingRooms = (service: Service) => {
    const selectedRoomIds = new Set(service.boardingMetadata?.selectedRooms ?? []);

    if (!service.rooms?.length || selectedRoomIds.size === 0) {
        return [];
    }

    return service.rooms
        .filter(room => selectedRoomIds.has(room.id))
        .map(room => ({
            ...room,
            room_units: room.room_units.filter(roomUnit => roomUnit.active)
        }))
        .filter(room => room.room_units.length > 0);
};

export const assignBoardingUnitsForPets = (
    rooms: Array<DaycareAvailabilityRoom>,
    pets: Array<Pet>
): Array<number> | null => {
    for (const room of rooms) {
        const roomUnitIds = getRoomUnitAssignmentsForPets(
            convertAvailabilityRoomToRoom(room),
            pets
        );

        if (roomUnitIds) {
            return roomUnitIds;
        }
    }

    return null;
};

export const getBoardingServiceRoomUnitAssignment = (
    service: Service,
    pets: Array<Pet>
): BoardingServiceRoomUnitAssignment | null => {
    if (!service.boardingMetadata?.roomsEnabled || pets.length === 0) {
        return null;
    }

    if (!pets.every(pet => service.petList.includes(pet.type.id))) {
        return null;
    }

    const assignedRooms = getAssignedBoardingRooms(service);

    for (const room of assignedRooms) {
        const roomUnitIds = getRoomUnitAssignmentsForPets(room, pets);

        if (roomUnitIds) {
            return {
                roomId: room.id,
                roomUnitIds
            };
        }
    }

    return null;
};

export const isBoardingServiceAvailableForPets = (
    service: Service,
    pets: Array<Pet>,
    nightsCount?: number
): boolean => {
    if (!service.boardingMetadata?.roomsEnabled) {
        return false;
    }

    if (service.boardingMetadata.selectedRooms.length !== 1) {
        console.warn(
            `Boarding service ${service.id} has ${service.boardingMetadata.selectedRooms.length} selected rooms; expected exactly 1. Skipping.`
        );
        return false;
    }

    // Skip min nights check when no range is selected yet (validated at DatePicker step).
    if (nightsCount !== undefined && nightsCount < (service.boardingMetadata.minNights ?? 1)) {
        return false;
    }

    return getBoardingServiceRoomUnitAssignment(service, pets) !== null;
};

export const isBoardingServiceAvailableForPet = (
    service: Service,
    pet: Pet,
    nightsCount?: number
): boolean => {
    return isBoardingServiceAvailableForPets(service, [pet], nightsCount);
};
